import React, { useEffect, useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  Tent, Home, Caravan, Mountain, Users, Trees, Bed,
  Sparkles, ArrowRight, Heart, MessageCircle,
} from "lucide-react";
import { api } from "../lib/api";

/**
 * /share/reels-variety — bright + colourful 7-day "ways to sleep outside" series.
 *
 * Design intent:
 *   • SUBTLE sign-up: no QR on every card, no hammered "free account" CTA.
 *     Only the final summary card gets a soft URL + QR — the rest are pure
 *     "wow, I didn't know this was an option" curiosity bait.
 *   • Vibrant SOLID gradients per camping type (no stock photos) — instant
 *     load, always crisp, gives the carousel a deliberate rainbow vibe.
 *   • Short, snappy 3-word hooks — designed for thumb-stop in a single beat.
 *   • Live count badge so the stats grow with the catalogue.
 */

const PUBLIC_URL = (process.env.REACT_APP_PUBLIC_URL || "https://camp-compass.uk").replace(/\/$/, "");

// One vibrant gradient per camping style — chosen to feel distinctively
// "this kind of trip" while staying readable for big white display type.
const SCENES = [
  {
    key: "tent",
    icon: Tent,
    gradient: "linear-gradient(155deg,#16a34a 0%,#84cc16 60%,#facc15 100%)",
    label: "Day 1 · Classic",
    hook: ["OLD", "SCHOOL."],
    sub: "Canvas. Fire. Stars.",
    stat: "10,000+ UK pitches",
    accent: "#facc15",
  },
  {
    key: "pod",
    icon: Home,
    gradient: "linear-gradient(155deg,#ec4899 0%,#f97316 100%)",
    label: "Day 2 · Glamp",
    hook: ["SLEEP", "IN A POD."],
    sub: "Warm. Weatherproof. Wow.",
    stat: "Glamping pods nationwide",
    accent: "#fde047",
  },
  {
    key: "van",
    icon: Caravan,
    gradient: "linear-gradient(155deg,#0ea5e9 0%,#3b82f6 60%,#6366f1 100%)",
    label: "Day 3 · Van life",
    hook: ["PARK.", "WAKE.", "REPEAT."],
    sub: "Campervan-friendly only? One toggle.",
    stat: "Filter campervan pitches in one tap",
    accent: "#fbbf24",
  },
  {
    key: "caravan",
    icon: Caravan,
    gradient: "linear-gradient(155deg,#f59e0b 0%,#ef4444 100%)",
    label: "Day 4 · Tow & go",
    hook: ["HOME,", "ON A", "HITCH."],
    sub: "Hardstanding · electric · serviced.",
    stat: "Sort by hookup, hardstanding, EHU",
    accent: "#fff7ed",
  },
  {
    key: "chalet",
    icon: Mountain,
    gradient: "linear-gradient(155deg,#8b5cf6 0%,#6366f1 60%,#0ea5e9 100%)",
    label: "Day 5 · Glamping chalet",
    hook: ["NOT", "YOUR DAD'S", "CAMPING."],
    sub: "Real bed. Hot shower. Wild view.",
    stat: "Chalets, lodges, cabins",
    accent: "#fde047",
  },
  {
    key: "hostel",
    icon: Users,
    gradient: "linear-gradient(155deg,#06b6d4 0%,#10b981 100%)",
    label: "Day 6 · Bunkhouse",
    hook: ["ROOMS", "FROM £15."],
    sub: "Hostels & bunkhouses, every county.",
    stat: "Hostels nationwide",
    accent: "#fde047",
  },
  {
    key: "bothy",
    icon: Trees,
    gradient: "linear-gradient(155deg,#064e3b 0%,#1f2937 100%)",
    label: "Day 7 · Off-grid",
    hook: ["JUST YOU", "+ WEATHER."],
    sub: "Bothies. Free. No reception.",
    stat: "Wild huts & basic shelters",
    accent: "#84cc16",
  },
];

/** 9:16 vertical scene wrapper — matches the existing ReelsKit format so it
 * imports cleanly into CapCut / Instagram. */
function Scene({ gradient, accent, children, index }) {
  return (
    <section
      className="relative w-full mx-auto flex flex-col justify-between text-white overflow-hidden shadow-2xl"
      style={{
        aspectRatio: "9 / 16",
        maxWidth: 540,
        background: gradient,
      }}
      data-testid={`reel-variety-${index}`}
    >
      {/* Decorative grain dots for richness — no asset load, pure CSS */}
      <div
        className="absolute inset-0 opacity-25 pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage:
            "radial-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)",
          backgroundSize: "14px 14px",
        }}
      />
      {/* Big soft orb accent — adds depth without competing with copy */}
      <div
        className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full blur-3xl opacity-40 pointer-events-none"
        style={{ background: accent }}
      />
      {children}
    </section>
  );
}

function StickerBadge({ children, className = "", bg = "rgba(0,0,0,0.55)" }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.2em] text-white backdrop-blur-sm ${className}`}
      style={{ background: bg }}
    >
      {children}
    </span>
  );
}

// 7-day poll rotation — kept in sync with backend POLLS_BY_WEEKDAY so the
// caption pack & schedule cards below can reference the exact rotation
// without an API call. The live poll scene (`PollScene`) still pulls
// /polls/today so it auto-updates as the rotation advances and so any
// future poll-config tweak (question/options) flows through with no
// reel-side edit.
const POLL_ROTATION = [
  { day: "Monday",    pid: "weekend-region",  hint: "Where this weekend?" },
  { day: "Tuesday",   pid: "dealbreakers",    hint: "Must-have facility?" },
  { day: "Wednesday", pid: "camping-style",   hint: "Camping vibe?" },
  { day: "Thursday",  pid: "when-escaping",   hint: "When are you off?" },
  { day: "Friday",    pid: "tonight-outfit",  hint: "Sleeping in tonight?" },
  { day: "Saturday",  pid: "dog-policy",      hint: "Dog joining you?" },
  { day: "Sunday",    pid: "why-camp",        hint: "Why do you camp?" },
];

/** Split a poll question into 2–3 punchy lines for the giant hero type.
 * Tries comma → "/" → naive word balance so we never wrap awkwardly. */
function splitHook(question) {
  if (!question) return ["WHERE", "ARE YOU", "OFF TO?"];
  const q = question.replace(/\s+/g, " ").trim().toUpperCase();
  if (q.includes(",")) return q.split(",").map((s) => s.trim());
  if (q.includes("/")) return q.split("/").map((s) => s.trim());
  const words = q.split(" ");
  if (words.length <= 3) return [q];
  if (words.length <= 6) {
    const mid = Math.ceil(words.length / 2);
    return [words.slice(0, mid).join(" "), words.slice(mid).join(" ")];
  }
  // 3 lines for longer questions
  const third = Math.ceil(words.length / 3);
  return [
    words.slice(0, third).join(" "),
    words.slice(third, third * 2).join(" "),
    words.slice(third * 2).join(" "),
  ];
}

/** Live poll scene — fetches today's poll on mount and renders the actual
 * question + option chips. Auto-updates as the 7-day rotation advances
 * AND as any new option label is edited in the backend config.
 * Falls back to the regional poll on network error so the reel still
 * exports cleanly when offline. */
function PollScene({ index }) {
  const [poll, setPoll] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/polls/today").then(({ data }) => {
      if (alive && data?.poll_id) setPoll(data);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const question = poll?.question || "Where are you off to this weekend?";
  const hookLines = useMemo(() => splitHook(question), [question]);
  const options = (poll?.options || [
    { label: "Lake District" }, { label: "Cornwall" },
    { label: "Scotland" }, { label: "Wales" },
    { label: "Peak District" }, { label: "Yorkshire" },
  ]).slice(0, 6);

  return (
    <Scene
      index={index}
      gradient="linear-gradient(160deg,#1e3a8a 0%,#7c3aed 35%,#ec4899 70%,#f97316 100%)"
      accent="#fde047"
    >
      <div className="p-6 sm:p-8 pt-12 relative z-10">
        <StickerBadge bg="rgba(0,0,0,0.55)">
          <Sparkles className="w-3.5 h-3.5" /> Today&apos;s poll · 1 tap
        </StickerBadge>
      </div>
      <div className="px-6 sm:px-8 text-center relative z-10">
        <div
          className={
            hookLines.length >= 3
              ? "font-display text-[44px] sm:text-[60px] leading-[0.95] drop-shadow-[0_4px_18px_rgba(0,0,0,0.55)]"
              : "font-display text-[52px] sm:text-[72px] leading-[0.92] drop-shadow-[0_4px_18px_rgba(0,0,0,0.55)]"
          }
          data-testid="poll-reel-question"
        >
          {hookLines.map((line, i) => (
            <React.Fragment key={i}>
              {line}
              {i < hookLines.length - 1 ? <br /> : null}
            </React.Fragment>
          ))}
        </div>
        <div className="mt-4 font-bold text-base sm:text-xl text-[#fde047]">
          Scan. Tap. See campsites.
        </div>
        <div
          className="mt-3 grid grid-cols-2 gap-2 max-w-[300px] mx-auto text-[11px] mono"
          data-testid="poll-reel-options"
        >
          {options.map((o, i) => (
            <div
              key={o.key || o.label || i}
              className="px-2 py-1.5 bg-white/15 border border-white/30 backdrop-blur-sm"
            >
              {o.label}
            </div>
          ))}
        </div>
      </div>
      <div className="p-6 sm:p-8 flex items-end justify-between gap-4 relative z-10">
        <div>
          <div className="text-[10px] mono uppercase tracking-[0.25em] text-white/90 mb-1 inline-flex items-center gap-1">
            Vote now <ArrowRight className="w-3 h-3" />
          </div>
          <div className="font-display text-lg">camp-compass.uk/vote</div>
        </div>
        <div className="bg-white p-2 shrink-0" data-testid="reel-vote-qr">
          <QRCodeSVG value={`${PUBLIC_URL}/vote`} size={96} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
        </div>
      </div>
    </Scene>
  );
}

export default function ReelsVariety() {
  const [count, setCount] = useState(22159);
  useEffect(() => {
    let alive = true;
    api.get("/campsites/stats").then(({ data }) => {
      if (alive && data?.total) setCount(data.total);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const fmt = useMemo(() => count.toLocaleString("en-GB"), [count]);

  return (
    <div className="bg-[#0e0e0e] min-h-screen text-white" data-testid="reels-variety-page">
      <header className="max-w-3xl mx-auto px-5 py-8">
        <div className="eyebrow text-[#fde047] mb-2">Reels Kit · Ways to camp</div>
        <h1 className="font-display text-3xl sm:text-4xl mb-3">7 ways to sleep outside.</h1>
        <p className="text-sm text-[#bbb] leading-relaxed max-w-xl">
          A 7-day variety series — one camping style per reel. No aggressive sign-up CTA on
          individual cards; just bright colour, a punchy hook, and curiosity. The full sign-up
          nudge lives only on the final summary card so the series feels like inspiration,
          not a sales pitch.
        </p>
      </header>

      <div className="flex flex-col items-center gap-12 px-3 pb-12">
        {/* DAY 0 — TEASER (drops the day before Day 1 — seeds curiosity for the
            whole series). High-contrast electric purple, big question mark vibe. */}
        <Scene
          index={0}
          gradient="linear-gradient(155deg,#0f172a 0%,#7c3aed 60%,#ec4899 100%)"
          accent="#fde047"
        >
          <div className="p-6 sm:p-8 pt-12 relative z-10">
            <StickerBadge bg="rgba(255,255,255,0.18)">
              <Sparkles className="w-3.5 h-3.5" /> Day 0 · Teaser
            </StickerBadge>
          </div>
          <div className="px-6 sm:px-8 text-center relative z-10">
            <div
              className="font-display leading-[0.85] mb-2 drop-shadow-[0_4px_18px_rgba(0,0,0,0.6)]"
              style={{ fontSize: "clamp(96px, 22vw, 220px)", color: "#fde047" }}
            >
              ?
            </div>
            <div className="font-display text-[42px] sm:text-[62px] leading-[0.95] drop-shadow-[0_4px_18px_rgba(0,0,0,0.55)]">
              HOW MANY<br/>WAYS CAN<br/>YOU SLEEP<br/>OUTSIDE?
            </div>
            <div className="mt-4 font-bold text-base sm:text-xl text-[#fde047]">
              Guess in the comments. Answer drops tomorrow.
            </div>
          </div>
          <div className="p-6 sm:p-8 relative z-10">
            <div className="flex items-center justify-between gap-3 border-t border-white/30 pt-3">
              <div className="text-[10px] mono uppercase tracking-[0.3em] text-white/80">
                7-day series starts tomorrow
              </div>
              <div className="font-display text-base text-white/95">camp-compass.uk</div>
            </div>
          </div>
        </Scene>

        {SCENES.map((s, i) => {
          const Icon = s.icon;
          return (
            <Scene key={s.key} index={i + 1} gradient={s.gradient} accent={s.accent}>
              <div className="p-6 sm:p-8 pt-12 relative z-10">
                <StickerBadge bg="rgba(0,0,0,0.4)">
                  <Sparkles className="w-3.5 h-3.5" /> {s.label}
                </StickerBadge>
              </div>

              <div className="px-6 sm:px-8 relative z-10">
                <Icon
                  className="w-28 h-28 sm:w-40 sm:h-40 mb-4 mx-auto drop-shadow-[0_4px_24px_rgba(0,0,0,0.45)]"
                  strokeWidth={1.4}
                  style={{ color: s.accent }}
                />
                <div className="font-display text-[56px] sm:text-[78px] leading-[0.92] text-center drop-shadow-[0_4px_18px_rgba(0,0,0,0.55)]">
                  {s.hook.map((line, j) => (
                    <div key={j}>{line}</div>
                  ))}
                </div>
                <div
                  className="text-base sm:text-xl font-bold leading-snug mt-3 text-center"
                  style={{ color: s.accent }}
                >
                  {s.sub}
                </div>
              </div>

              {/* Subtle footer — just the count + brand mark, NO QR, NO "sign up". */}
              <div className="p-6 sm:p-8 relative z-10">
                <div className="flex items-center justify-between gap-3 border-t border-white/30 pt-3">
                  <div className="text-[10px] mono uppercase tracking-[0.3em] text-white/80">
                    {s.stat}
                  </div>
                  <div className="font-display text-base text-white/95">camp-compass.uk</div>
                </div>
              </div>
            </Scene>
          );
        })}

        {/* FINAL SUMMARY — the only card with a real signup nudge. Rainbow vibe. */}
        <Scene
          index={SCENES.length + 1}
          gradient="linear-gradient(135deg,#ec4899 0%,#f97316 20%,#facc15 40%,#10b981 60%,#0ea5e9 80%,#8b5cf6 100%)"
          accent="#ffffff"
        >
          <div className="p-6 sm:p-8 pt-12 relative z-10">
            <StickerBadge bg="rgba(0,0,0,0.55)">
              <Sparkles className="w-3.5 h-3.5" /> Pick yours
            </StickerBadge>
          </div>
          <div className="px-6 sm:px-8 text-center relative z-10">
            <div className="font-display text-[56px] sm:text-[80px] leading-[0.92] drop-shadow-[0_4px_18px_rgba(0,0,0,0.45)]">
              9 WAYS<br/>TO SLEEP<br/>OUTSIDE.
            </div>
            <div className="mt-4 font-display text-2xl text-white/95">
              One map. {fmt} UK sites.
            </div>
            <div className="mt-2 text-sm sm:text-base text-white/85">
              Tent · Pod · Van · Caravan · Chalet · Hostel · Motel · Guest house · Bothy
            </div>
          </div>
          <div className="p-6 sm:p-8 flex items-end justify-between gap-4 relative z-10">
            <div>
              <div className="text-[10px] mono uppercase tracking-[0.25em] text-white/90 mb-1 inline-flex items-center gap-1">
                Find yours <ArrowRight className="w-3 h-3" />
              </div>
              <div className="font-display text-lg">camp-compass.uk</div>
            </div>
            <div className="bg-white p-2 shrink-0">
              <QRCodeSVG value={`${PUBLIC_URL}/browse`} size={84} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
            </div>
          </div>
        </Scene>

        {/* INTERACTIVE VOTE — drives QR-scan traffic to /vote where a single
            tap registers a vote AND lands the user in the matching Browse view.
            This scene auto-pulls TODAY's poll so the question + options stay
            in sync with the 7-day rotation (and any future edits in the
            backend POLLS_CONFIG) without any code change to this file. */}
        <PollScene index={SCENES.length + 1.5} />

        {/* DAY 9 — COMMUNITY / SHARE YOUR STORY (warm closer). Soft signup:
            "browse free, sign up when satisfied, share in the threads". */}        <Scene
          index={SCENES.length + 2}
          gradient="linear-gradient(165deg,#fda4af 0%,#fb923c 35%,#fbbf24 65%,#fde68a 100%)"
          accent="#7c2d12"
        >
          <div className="p-6 sm:p-8 pt-12 relative z-10">
            <StickerBadge bg="rgba(124,45,18,0.55)">
              <Heart className="w-3.5 h-3.5" /> Day 9 · We want to hear from you
            </StickerBadge>
          </div>
          <div className="px-6 sm:px-8 text-center relative z-10 text-[#451a03]">
            <Heart className="w-24 h-24 sm:w-32 sm:h-32 mb-4 mx-auto" strokeWidth={1.6} fill="#dc2626" color="#dc2626" />
            <div className="font-display text-[48px] sm:text-[68px] leading-[0.92] drop-shadow-[0_2px_10px_rgba(255,255,255,0.45)]">
              BROWSE.<br/>SIGN UP<br/>WHEN READY.
            </div>
            <div className="mt-4 font-bold text-base sm:text-xl text-[#7c2d12] leading-snug">
              Then share your story in the threads.
            </div>
            <div className="mt-3 text-sm sm:text-base text-[#9a3412] max-w-[28ch] mx-auto leading-relaxed">
              The best campsites are the ones a real camper just got back from.
              Drop a photo, drop a tip, help the next family find their pitch.
              ❤️🏕️⛺
            </div>
          </div>
          <div className="p-6 sm:p-8 flex items-end justify-between gap-4 relative z-10">
            <div className="text-[#451a03]">
              <div className="text-[10px] mono uppercase tracking-[0.25em] mb-1 inline-flex items-center gap-1">
                <MessageCircle className="w-3 h-3" /> Join the threads
              </div>
              <div className="font-display text-lg">camp-compass.uk</div>
            </div>
            <div className="bg-white p-2 shrink-0">
              <QRCodeSVG value={`${PUBLIC_URL}/threads`} size={84} bgColor="#ffffff" fgColor="#111111" includeMargin={false} />
            </div>
          </div>
        </Scene>
      </div>

      {/* SCRIPT KIT — 7-day caption pack */}
      <section className="max-w-3xl mx-auto px-5 pb-20 space-y-6 text-white" data-testid="reel-variety-scripts">
        <div className="border-l-2 border-[#fde047] pl-4">
          <h2 className="font-display text-2xl mb-2">7-day caption pack</h2>
          <p className="text-sm text-[#bbb]">
            Post Day 1 → Day 7 on consecutive days for a "ways to camp" series. The QR-only
            CTA is on Day 8 (summary) — so the audience watches all 7 first.
          </p>
        </div>

        {[
          {
            day: "Day 0 · Teaser (post day BEFORE Day 1) 🔮",
            hook: "How many ways can you sleep outside? Guess in the comments.",
            caption: `How many ways can you actually sleep outside in the UK? 🤔\n\nGuess in the comments. We'll start revealing one a day.\n\nClue: it's a LOT more than just tents.\n\n7-day series starts tomorrow. 🔗 camp-compass.uk`,
            hashtags: "#guessthecount #ukcamping #campingseries #campsite #camping #ukoutdoors #weekendgetaway #campingweekend #ukstaycation #greatoutdoors",
            best: "Sunday 7pm — feeds the planning peak for Mon-onwards",
            sound: "Suspense build / curious uplift",
          },
          {
            day: "Day 1 · Classic tent",
            hook: "POV: it's just you, canvas, and a fire.",
            caption: "POV: it's just you, canvas, and a fire. 🔥\n\nNo wifi. No notifications. Just the wind and the stars.\n\nIf this is your version of a perfect weekend, we mapped 10,000+ UK pitches for you.\n\n🔗 camp-compass.uk",
            hashtags: "#tentlife #ukcamping #wildcamping #campervan #campfire #ukoutdoors #campingweekend #campsite #campinguk #greatoutdoors",
            best: "Sunday 7pm — Monday planning peak",
            sound: "Crackling fire / acoustic mellow",
          },
          {
            day: "Day 2 · Glamping pod",
            hook: "Camping but make it cosy.",
            caption: "Camping but make it cosy. 🛖✨\n\nA pod = a heated, weatherproof, wifi-able tent. You still wake up to birdsong, but your back doesn't ache.\n\nFiltered by pod-only on the map. Easy.\n\n🔗 camp-compass.uk",
            hashtags: "#glampinguk #campingpod #glamping #ukstaycation #romanticgetaway #weekendvibes #cosyvibes #ukoutdoors #glampingholiday #couplesgetaway",
            best: "Wednesday 8pm — couples planning peak",
            sound: "Soft indie pop / cosy fairy-light vibe",
          },
          {
            day: "Day 3 · Campervan",
            hook: "Park anywhere. Wake up everywhere.",
            caption: "Park anywhere. Wake up everywhere. 🚐\n\nWe added a 'campervan-friendly' toggle to the filter — one tap and the map only shows you sites that actually fit a van.\n\nNo more 'sorry, we're tent-only' surprises at 9pm.\n\n🔗 camp-compass.uk",
            hashtags: "#vanlifeuk #campervanlife #campervan #vanlife #ukvanlife #vwt6 #motorhome #ukroadtrip #vanlifediaries #vanlifeideas",
            best: "Friday 5pm — pre-weekend road-trip itch",
            sound: "Upbeat road-trip indie",
          },
          {
            day: "Day 4 · Caravan",
            hook: "Home, on a hitch.",
            caption: "Home, on a hitch. 🚙🏠\n\nHardstanding pitches. Electric hookup. Serviced sites with grey water + freshwater taps.\n\nFilter the map by 'caravan' + 'hardstanding' + 'EHU' and book the same evening.\n\n🔗 camp-compass.uk",
            hashtags: "#caravanlife #caravanuk #touringcaravan #caravanholiday #ukstaycation #caravanclub #serviced #hardstanding #ukcamping #familyholiday",
            best: "Tuesday 7pm — caravan-club browsing peak",
            sound: "Warm acoustic / family vibe",
          },
          {
            day: "Day 5 · Glamping chalet",
            hook: "Not your dad's camping.",
            caption: "Not your dad's camping. 🛁🌲\n\nReal bed. Hot shower. Log burner. Hot tub on the porch. Still surrounded by woodland.\n\nGlamping chalets, lodges and cabins — filterable on the map.\n\n🔗 camp-compass.uk",
            hashtags: "#glamping #glampinguk #luxurycamping #cabinlife #shepherdshut #hottub #romanticbreak #ukgetaway #lodgelife #campingluxe",
            best: "Thursday 8pm — weekend-luxury research",
            sound: "Cinematic golden-hour acoustic",
          },
          {
            day: "Day 6 · Hostel / bunkhouse",
            hook: "Rooms from £15.",
            caption: "Rooms from £15. 🛏️\n\nDidn't pack the tent? Booked too late? A hostel or bunkhouse is still cheaper than a B&B and you'll meet way more interesting people.\n\nWe added 750+ UK hostels to the map this week.\n\n🔗 camp-compass.uk",
            hashtags: "#hosteluk #bunkhouse #budgettravel #ukbackpacking #hostellife #cheaptravel #ukhostels #backpacker #youthhostel #solotravel",
            best: "Saturday 11am — last-minute-trip panic",
            sound: "Backpacker indie / cheerful",
          },
          {
            day: "Day 7 · Bothy / off-grid",
            hook: "Just you + weather.",
            caption: "Just you + weather. 🌧️🏔️\n\nFree mountain bothies and basic shelters scattered across Scotland, Wales and the Lakes.\n\nNo bookings. No bill. Bring a sleeping bag, a stove, and respect.\n\nWe mapped the lot.\n\n🔗 camp-compass.uk",
            hashtags: "#wildcamping #bothy #mountainbothy #scotlandhiking #wildcampinguk #munrobagging #mountains #offgrid #peakdistrict #lakedistrict",
            best: "Sunday 9am — Monday-dream escapism",
            sound: "Cinematic mountain / atmospheric",
          },
          {
            day: "Day 8 · Pick yours (summary + signup nudge)",
            hook: "9 ways to sleep outside.",
            caption: `9 ways to sleep outside. One map. ${fmt} UK sites. 🌍\n\nTent · Pod · Van · Caravan · Chalet · Hostel · Motel · Guest house · Bothy.\n\nWhichever your style is, we've already filtered the UK down to the ones that match.\n\nFree to use. No app store download. 🔗 camp-compass.uk`,
            hashtags: "#ukcamping #campsite #campervan #glamping #ukstaycation #pickyourstyle #freedom #weekendready #ukoutdoors #findyourpitch",
            best: "Monday 6pm — post-series conversion peak",
            sound: "Trending energetic + colour-pop edit",
          },
          {
            day: "Day 8.5 · Vote in 1 tap (interactive) 🗳️",
            hook: "Scan. Tap. See campsites. No signup.",
            caption: `Where are YOU off to this weekend? 🗺️\n\nScan the QR → tap one region → we instantly show you every campsite in that area. No signup. No app. No 7 dropdowns.\n\nLakes · Cornwall · Scotland · Wales · Peak District · Yorkshire — your pick.\n\n🔗 camp-compass.uk/vote`,
            hashtags: "#pollday #ukcamping #pickyourpitch #weekendescape #campsite #campinguk #greatoutdoors #ukstaycation #weekendvibes #onetapwonder",
            best: "Friday 4pm — peak weekend-planning scroll",
            sound: "Upbeat playful indie / curiosity-build",
          },
          {
            day: "Day 9 · Share your story (warm closer) ❤️",
            hook: "Browse free. Sign up when you're satisfied. Then tell us a story.",
            caption: `Browse the whole map for free — no account needed. 🗺️\n\nWhen you're satisfied we've actually helped you, sign up (takes 20 seconds, no card, no app store).\n\nThen come share your story in the threads. Where did you pitch? What did you wish you'd packed? Which site has the best showers? We genuinely want to hear from you.\n\nThe best campsites are the ones a real camper just got back from. ❤️🏕️⛺\n\n🔗 camp-compass.uk`,
            hashtags: "#campingstories #ukcamping #campingcommunity #realcampers #sharethejourney #campingmemories #campingforum #campingtips #ukcampers #campingfamily",
            best: "Tuesday 7pm — post-trip nostalgia + planning crossover",
            sound: "Warm fingerstyle acoustic / nostalgic indie",
          },
        ].map((s, i) => (
          <article key={i} className="p-5 border hairline bg-[#1a1a1a]" data-testid={`variety-script-${i + 1}`}>
            <div className="text-[10px] mono uppercase tracking-[0.25em] text-[#fde047] mb-2">{s.day}</div>
            <div className="font-display text-lg mb-3">{s.hook}</div>
            <div className="bg-[#0e0e0e] p-3 mb-3 whitespace-pre-wrap text-sm leading-relaxed">{s.caption}</div>
            <div className="grid sm:grid-cols-2 gap-3 text-xs">
              <div className="bg-[#0e0e0e] p-3">
                <div className="eyebrow text-[#888] mb-1">Hashtags</div>
                <div className="text-[#bbb] break-words leading-relaxed">{s.hashtags}</div>
              </div>
              <div className="space-y-2">
                <div className="bg-[#0e0e0e] p-3">
                  <div className="eyebrow text-[#888] mb-1">Best time to post</div>
                  <div className="text-white">{s.best}</div>
                </div>
                <div className="bg-[#0e0e0e] p-3">
                  <div className="eyebrow text-[#888] mb-1">Sound vibe</div>
                  <div className="text-white">{s.sound}</div>
                </div>
              </div>
            </div>
          </article>
        ))}

        <div className="p-5 border-2 border-[#a78bfa] bg-[#1a1a1a]" data-testid="poll-rotation-preview">
          <h3 className="font-display text-xl mb-1">Daily poll rotation (auto-refreshes)</h3>
          <p className="text-xs text-[#bbb] mb-3 leading-relaxed">
            The poll scene above pulls <span className="mono text-[#fde047]">/api/polls/today</span> on
            every page load — whichever poll is active today is what the reel shows. Edit the
            backend <span className="mono text-[#fde047]">POLLS_CONFIG</span> question/options and
            this scene updates without any code change here.
          </p>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-sm">
            {POLL_ROTATION.map((r) => (
              <li key={r.pid} className="flex items-center gap-2 bg-[#0e0e0e] px-3 py-2">
                <span className="mono text-[10px] uppercase tracking-[0.2em] text-[#a78bfa] w-[72px]">{r.day}</span>
                <span className="text-white text-[13px]">{r.hint}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="p-5 border-2 border-[#fde047] bg-[#1a1a1a]" data-testid="reel-variety-tips">
          <h3 className="font-display text-xl mb-2">How to film this in 30 minutes</h3>
          <ul className="space-y-2 text-sm text-[#ddd] leading-relaxed list-decimal pl-5">
            <li>Open this page on your phone, scroll to each scene, screenshot it.</li>
            <li>Drop all 8 screenshots into CapCut / Instagram Reels as a slideshow.</li>
            <li>Add one trending sound and the per-day caption — schedule one a day for a week. The colour swap between cards is the visual hook.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}
